<div class="stacksets-instructions">
  @if (!hideHeader) {
    <h2>Deploy Access with AWS StackSets</h2>
    <p class="intro">
      Use CloudFormation StackSets to deploy {{ uiService.productName }} access across multiple AWS accounts from a
      central management account.
    </p>
  }

  <!-- Steps 1-3: Hidden once migration is complete -->
  @if (!migrationComplete) {
    <!-- Warning if existing CloudFormation stacks may conflict -->
    @if (hasSourceCfnButNotStackSet || hasTargetCfnButNotStackSet) {
      <div class="warning-notice migration-warning">
        <i class="fa-solid fa-circle-exclamation"></i>
        <span>
          <strong>Existing CloudFormation stacks detected.</strong>
          @if (hasSourceCfnButNotStackSet && hasTargetCfnButNotStackSet) {
            Stacks <code>{{ sourceStackName }}</code> and <code>{{ targetStackName }}</code> exist
          }
          @if (hasSourceCfnButNotStackSet && !hasTargetCfnButNotStackSet) {
            Stack <code>{{ sourceStackName }}</code> exists
          }
          @if (!hasSourceCfnButNotStackSet && hasTargetCfnButNotStackSet) {
            Stack <code>{{ targetStackName }}</code> exists
          }
          in your account
          @if (hasSourceCfnButNotStackSet && hasTargetCfnButNotStackSet) {
            s
          }
          and may cause conflicts during StackSets deployment. Remove them before proceeding to avoid conflicts.
        </span>
        <button type="button" class="arpio-secondary-button small-button" (click)="onRecheckAccess()">RECHECK</button>
      </div>
    }
    <!-- Step 1: Configuration — collected once, drives every command in the steps below -->
    <div class="step-section" [class.expanded]="isExpanded('config')">
      <div class="step-header" [style.top.px]="stickyOffset || null" (click)="toggleSection('config')">
        <div class="step-number">1</div>
        <div class="step-title">
          <h3>Configuration</h3>
          <p class="step-subtitle">
            Fill these in once. Values auto-populate every command in steps 2–4 (including troubleshooting).
          </p>
        </div>
        <i
          class="fa-solid"
          [class.fa-chevron-down]="isExpanded('config')"
          [class.fa-chevron-right]="!isExpanded('config')"
        ></i>
      </div>
      <div class="step-content-wrapper" [@slideDown]="isExpanded('config') ? 'expanded' : 'collapsed'">
        <div class="step-content">
          <div class="stacksets-config-card">
            <label class="topology-toggle">
              <input
                type="checkbox"
                class="arpio-checkbox"
                [checked]="!isTwoMgmtAccounts"
                (change)="onSingleMgmtToggle($event)"
              />
              <span class="topology-toggle-label">
                <strong>Use a single management account for both Primary and Recovery</strong>
                <span class="topology-toggle-hint"
                  >Leave unchecked if Primary and Recovery are deployed from separate management accounts.</span
                >
              </span>
            </label>
            <div class="topology-inputs" [class.is-single]="!isTwoMgmtAccounts" [class.is-separate]="isTwoMgmtAccounts">
              <!-- Single-mgmt mode: one shared track wrapping the combined header
              and a single row with the shared mgmt account input plus both
              StackSet inputs side-by-side. -->
              @if (!isTwoMgmtAccounts) {
                <div class="topology-track">
                  <div class="topology-subheader">Primary &amp; Recovery</div>
                  <div class="topology-row">
                    <div class="topology-cell mgmt">
                      <label for="management-account-id">
                        <i class="fa-solid fa-shield-halved"></i>
                        Management Account ID
                      </label>
                      <input
                        type="text"
                        id="management-account-id"
                        [(ngModel)]="primaryManagementAccountId"
                        placeholder="e.g., 123456789012"
                        (keydown.enter)="$event.preventDefault()"
                      />
                    </div>
                    <div class="topology-cell stackset">
                      <label for="primary-stackset-name">
                        <i class="fa-solid fa-location-dot"></i>
                        Primary Access StackSet Name
                      </label>
                      <input
                        type="text"
                        id="primary-stackset-name"
                        [(ngModel)]="primaryStackSetName"
                        placeholder="e.g., ArpioPrimaryAccess"
                        (keydown.enter)="$event.preventDefault()"
                      />
                    </div>
                    <div class="topology-cell stackset">
                      <label for="recovery-stackset-name">
                        <i class="fa-solid fa-location-crosshairs"></i>
                        Recovery Access StackSet Name
                      </label>
                      <input
                        type="text"
                        id="recovery-stackset-name"
                        [(ngModel)]="recoveryStackSetName"
                        placeholder="e.g., ArpioRecoveryAccess"
                        (keydown.enter)="$event.preventDefault()"
                      />
                    </div>
                  </div>
                </div>
              }
              <!-- Two-mgmt mode: two side-by-side tracks, each owning its subheader and inputs. -->
              @if (isTwoMgmtAccounts) {
                <div class="topology-track">
                  <div class="topology-subheader">Primary</div>
                  <div class="topology-cell mgmt">
                    <label for="primary-management-account-id">
                      <i class="fa-solid fa-shield-halved"></i>
                      Primary Management Account ID
                    </label>
                    <input
                      type="text"
                      id="primary-management-account-id"
                      [(ngModel)]="primaryManagementAccountId"
                      placeholder="e.g., 123456789012"
                      (keydown.enter)="$event.preventDefault()"
                    />
                  </div>
                  <div class="topology-cell stackset">
                    <label for="primary-stackset-name">
                      <i class="fa-solid fa-location-dot"></i>
                      Primary Access StackSet Name
                    </label>
                    <input
                      type="text"
                      id="primary-stackset-name"
                      [(ngModel)]="primaryStackSetName"
                      placeholder="e.g., ArpioPrimaryAccess"
                      (keydown.enter)="$event.preventDefault()"
                    />
                  </div>
                </div>
                <div class="topology-track">
                  <div class="topology-subheader">Recovery</div>
                  <div class="topology-cell mgmt">
                    <label for="recovery-management-account-id">
                      <i class="fa-solid fa-shield-halved"></i>
                      Recovery Management Account ID
                    </label>
                    <input
                      type="text"
                      id="recovery-management-account-id"
                      [(ngModel)]="recoveryManagementAccountId"
                      placeholder="e.g., 123456789012"
                      (keydown.enter)="$event.preventDefault()"
                    />
                  </div>
                  <div class="topology-cell stackset">
                    <label for="recovery-stackset-name">
                      <i class="fa-solid fa-location-crosshairs"></i>
                      Recovery Access StackSet Name
                    </label>
                    <input
                      type="text"
                      id="recovery-stackset-name"
                      [(ngModel)]="recoveryStackSetName"
                      placeholder="e.g., ArpioRecoveryAccess"
                      (keydown.enter)="$event.preventDefault()"
                    />
                  </div>
                </div>
              }
            </div>
          </div>
          <div class="step-continue-row">
            <button type="button" class="arpio-secondary-button" (click)="continueToStep('config', 'prerequisites')">
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Step 2: Prerequisites -->
    <div class="step-section" [class.expanded]="isExpanded('prerequisites')">
      <div class="step-header" [style.top.px]="stickyOffset || null" (click)="toggleSection('prerequisites')">
        <div class="step-number">2</div>
        <div class="step-title">
          <h3>Set Up IAM Roles</h3>
          <p class="step-subtitle">
            One-time setup in your management
            @if (isTwoMgmtAccounts) {
              accounts
            }
            @if (!isTwoMgmtAccounts) {
              account
            }
            and member accounts
          </p>
        </div>
        <i
          class="fa-solid"
          [class.fa-chevron-down]="isExpanded('prerequisites')"
          [class.fa-chevron-right]="!isExpanded('prerequisites')"
        ></i>
      </div>
      <div class="step-content-wrapper" [@slideDown]="isExpanded('prerequisites') ? 'expanded' : 'collapsed'">
        <div class="step-content">
          <p>StackSets require two IAM roles to operate:</p>
          <div class="role-cards">
            <div class="role-card">
              <div class="role-header">
                <i class="fa-solid fa-shield-halved"></i>
                <strong>Administration Role</strong>
              </div>
              @if (!isTwoMgmtAccounts) {
                <p>
                  Deploy once in your <strong>management account</strong>. Allows CloudFormation to assume roles in
                  member accounts.
                </p>
              }
              @if (isTwoMgmtAccounts) {
                <p>
                  Deploy once in <strong>each management account</strong>. Each one allows CloudFormation to assume
                  roles in its corresponding member account.
                </p>
              }
            </div>
            <div class="role-card">
              <div class="role-header">
                <i class="fa-solid fa-share-nodes"></i>
                <strong>Execution Role</strong>
              </div>
              <p>
                Deploy in
                <strong>{{ uniqueAccountIds.length === 1 ? 'the member account' : 'each member account' }}</strong
                >:
                @for (accountId of uniqueAccountIds; track accountId; let last = $last) {
                  <code
                    >{{ accountId }}
                    @if (!last) {
                      and
                    }
                  </code>
                }
              </p>
            </div>
          </div>
          <!-- Create Administration Role -->
          @if (!isTwoMgmtAccounts) {
            <div class="command-group">
              <h4>
                <i class="fa-solid fa-terminal"></i>
                Create Administration Role
              </h4>
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span>Run this command from your <strong>management account</strong>.</span>
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button class="copy-btn" [copy-clipboard]="adminRoleCommand" (copied)="onCopied('admin-role')">
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('admin-role')"
                      [class.fa-check]="isCopied('admin-role')"
                    ></i>
                    {{ isCopied('admin-role') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('admin-role', adminRoleCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ adminRoleCommand }}</code></pre>
              </div>
            </div>
          }
          @if (isTwoMgmtAccounts) {
            <div class="command-group">
              <h4>
                <i class="fa-solid fa-terminal"></i>
                Create Administration Role &mdash; Primary management account
              </h4>
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Primary management account</strong> (<code>{{
                    primaryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button
                    class="copy-btn"
                    [copy-clipboard]="adminRoleCommand"
                    (copied)="onCopied('admin-role-primary')"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('admin-role-primary')"
                      [class.fa-check]="isCopied('admin-role-primary')"
                    ></i>
                    {{ isCopied('admin-role-primary') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('admin-role-primary', adminRoleCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ adminRoleCommand }}</code></pre>
              </div>
            </div>
            <div class="command-group">
              <h4>
                <i class="fa-solid fa-terminal"></i>
                Create Administration Role &mdash; Recovery management account
              </h4>
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Recovery management account</strong> (<code>{{
                    recoveryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button
                    class="copy-btn"
                    [copy-clipboard]="adminRoleCommand"
                    (copied)="onCopied('admin-role-recovery')"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('admin-role-recovery')"
                      [class.fa-check]="isCopied('admin-role-recovery')"
                    ></i>
                    {{ isCopied('admin-role-recovery') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('admin-role-recovery', adminRoleCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ adminRoleCommand }}</code></pre>
              </div>
            </div>
          }
          <!-- Create Execution Role -->
          @if (!isTwoMgmtAccounts) {
            <div class="command-group">
              <h4>
                <i class="fa-solid fa-terminal"></i>
                Create Execution Role
              </h4>
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span>Run this command from each <strong>member account</strong>.</span>
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button class="copy-btn" [copy-clipboard]="executionRoleCommand" (copied)="onCopied('exec-role')">
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('exec-role')"
                      [class.fa-check]="isCopied('exec-role')"
                    ></i>
                    {{ isCopied('exec-role') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('exec-role', executionRoleCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ executionRoleCommand }}</code></pre>
              </div>
            </div>
          }
          @if (isTwoMgmtAccounts) {
            <div class="command-group">
              <h4>
                <i class="fa-solid fa-terminal"></i>
                Create Execution Role &mdash; Primary member account
              </h4>
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from the <strong>Primary member account</strong> (<code>{{ sourceAccountId }}</code
                  >). It trusts the Primary management account (<code>{{ primaryMgmtAccountPlaceholder }}</code
                  >).</span
                >
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button
                    class="copy-btn"
                    [copy-clipboard]="executionRoleCommandPrimary"
                    (copied)="onCopied('exec-role-primary')"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('exec-role-primary')"
                      [class.fa-check]="isCopied('exec-role-primary')"
                    ></i>
                    {{ isCopied('exec-role-primary') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('exec-role-primary', executionRoleCommandPrimary)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ executionRoleCommandPrimary }}</code></pre>
              </div>
            </div>
            @if (targetAccountId !== sourceAccountId) {
              <div class="command-group">
                <h4>
                  <i class="fa-solid fa-terminal"></i>
                  Create Execution Role &mdash; Recovery member account
                </h4>
                <div class="location-notice">
                  <i class="fa-solid fa-circle-info"></i>
                  <span
                    >Run this from the <strong>Recovery member account</strong> (<code>{{ targetAccountId }}</code
                    >). It trusts the Recovery management account (<code>{{ recoveryMgmtAccountPlaceholder }}</code
                    >).</span
                  >
                </div>
                <div class="command-block">
                  <div class="command-actions">
                    <button
                      class="copy-btn"
                      [copy-clipboard]="executionRoleCommandRecovery"
                      (copied)="onCopied('exec-role-recovery')"
                    >
                      <i
                        class="fa-utility fa-semibold"
                        [class.fa-copy]="!isCopied('exec-role-recovery')"
                        [class.fa-check]="isCopied('exec-role-recovery')"
                      ></i>
                      {{ isCopied('exec-role-recovery') ? 'Copied!' : 'Copy' }}
                    </button>
                    <button
                      class="download-btn"
                      (click)="downloadCommand('exec-role-recovery', executionRoleCommandRecovery)"
                      title="Download as shell script"
                    >
                      <i class="fa-solid fa-download"></i>
                      Download
                    </button>
                  </div>
                  <pre><code>{{ executionRoleCommandRecovery }}</code></pre>
                </div>
              </div>
            }
          }
          <p class="help-link">
            <a
              href="https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/stacksets-prereqs-self-managed.html"
              target="_blank"
            >
              <i class="fa-solid fa-external-link"></i>
              AWS StackSets Prerequisites Documentation
            </a>
          </p>
          <!-- Troubleshooting Section -->
          <details class="troubleshooting-section" #troubleshoot1>
            <summary>
              <i class="fa-solid fa-wrench"></i>
              <span>Troubleshooting Step 2</span>
              <i
                class="fa-solid chevron-icon"
                [class.fa-chevron-down]="troubleshoot1.open"
                [class.fa-chevron-right]="!troubleshoot1.open"
              ></i>
            </summary>
            <div class="troubleshooting-content">
              <p class="last-updated"><em>Last updated: February 2, 2026</em></p>
              <div class="ts-list">
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Confirm Administration Role was created in management account</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>
                      Run this from your <strong>management account</strong> to confirm the Administration Role was
                      created:
                    </p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="verifyAdminRoleCommand"
                          (copied)="onCopied('verify-admin-role')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('verify-admin-role')"
                            [class.fa-check]="isCopied('verify-admin-role')"
                          ></i>
                          {{ isCopied('verify-admin-role') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ verifyAdminRoleCommand }}</code></pre>
                    </div>
                    <p class="expected-output">
                      Expected: Returns role details including <code>RoleName: ArpioStackSetAdministrationRole</code>
                    </p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Confirm Execution Role was created in member accounts</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>Run this from <strong>each member account</strong> to confirm the Execution Role was created:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="verifyExecRoleCommand"
                          (copied)="onCopied('verify-exec-role')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('verify-exec-role')"
                            [class.fa-check]="isCopied('verify-exec-role')"
                          ></i>
                          {{ isCopied('verify-exec-role') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ verifyExecRoleCommand }}</code></pre>
                    </div>
                    <p class="expected-output">
                      Expected: Returns role details including <code>RoleName: ArpioStackSetExecutionRole</code>
                    </p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Fix "not authorized to perform sts:AssumeRole" errors</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>
                      The Execution Role must trust the matching <strong>management account's</strong> Administration
                      Role. Run this from a member account:
                    </p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="verifyExecRoleTrustCommand"
                          (copied)="onCopied('verify-exec-trust')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('verify-exec-trust')"
                            [class.fa-check]="isCopied('verify-exec-trust')"
                          ></i>
                          {{ isCopied('verify-exec-trust') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ verifyExecRoleTrustCommand }}</code></pre>
                    </div>
                    @if (!isTwoMgmtAccounts) {
                      <p class="expected-output">
                        Expected: The <code>Principal.AWS</code> should contain
                        <code
                          >arn:aws:iam::{{ primaryMgmtAccountPlaceholder }}:role/ArpioStackSetAdministrationRole</code
                        >
                      </p>
                    }
                    @if (isTwoMgmtAccounts) {
                      <p class="expected-output">
                        Expected: The <code>Principal.AWS</code> in the
                        <strong>Primary member account</strong> (<code>{{ sourceAccountId }}</code
                        >) should contain
                        <code
                          >arn:aws:iam::{{ primaryMgmtAccountPlaceholder }}:role/ArpioStackSetAdministrationRole</code
                        >, and the <strong>Recovery member account</strong> (<code>{{ targetAccountId }}</code
                        >) should contain
                        <code
                          >arn:aws:iam::{{ recoveryMgmtAccountPlaceholder }}:role/ArpioStackSetAdministrationRole</code
                        >.
                      </p>
                    }
                    <div class="troubleshooting-warning">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      <span
                        ><strong>Common mistake:</strong> If the trust policy points to the same account instead of the
                        correct management account, delete the stack and re-run the Execution Role command with the
                        right <code>AdministrationAccountId</code> parameter.</span
                      >
                    </div>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Debug why IAM role deployment failed</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>If a role command failed, check the CloudFormation stack status for error details:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="checkAdminStackStatusCommand"
                          (copied)="onCopied('check-admin-stack')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('check-admin-stack')"
                            [class.fa-check]="isCopied('check-admin-stack')"
                          ></i>
                          {{ isCopied('check-admin-stack') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ checkAdminStackStatusCommand }}</code></pre>
                    </div>
                    <p class="expected-output">
                      Expected: <code>StackStatus: CREATE_COMPLETE</code>. If <code>ROLLBACK_COMPLETE</code>, check
                      <code>StackStatusReason</code> for the error.
                    </p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Delete a ROLLBACK_COMPLETE stack before retrying</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>If a stack is in <code>ROLLBACK_COMPLETE</code> state, delete it before retrying:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="deleteAdminStackCommand"
                          (copied)="onCopied('delete-admin-stack')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('delete-admin-stack')"
                            [class.fa-check]="isCopied('delete-admin-stack')"
                          ></i>
                          {{ isCopied('delete-admin-stack') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ deleteAdminStackCommand }}</code></pre>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </details>
          <div class="step-continue-row">
            <button type="button" class="arpio-secondary-button" (click)="continueToStep('prerequisites', 'create')">
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Step 3: Create StackSets -->
    <div class="step-section" [class.expanded]="isExpanded('create')">
      <div class="step-header" [style.top.px]="stickyOffset || null" (click)="toggleSection('create')">
        <div class="step-number">3</div>
        <div class="step-title">
          <h3>Create StackSets</h3>
          <p class="step-subtitle">
            One-time setup in your management
            @if (isTwoMgmtAccounts) {
              accounts
            }
            @if (!isTwoMgmtAccounts) {
              account
            }
          </p>
        </div>
        <i
          class="fa-solid"
          [class.fa-chevron-down]="isExpanded('create')"
          [class.fa-chevron-right]="!isExpanded('create')"
        ></i>
      </div>
      <div class="step-content-wrapper" [@slideDown]="isExpanded('create') ? 'expanded' : 'collapsed'">
        <div class="step-content">
          @if (!isTwoMgmtAccounts) {
            <div class="important-note">
              <i class="fa-solid fa-circle-info"></i>
              <span> Run these commands from your <strong>management account</strong>. </span>
            </div>
          }
          @if (isTwoMgmtAccounts) {
            <div class="important-note">
              <i class="fa-solid fa-circle-info"></i>
              <span>
                Run each command from its matching management account. The Primary StackSet is created from your
                <strong>Primary management account</strong> (<code>{{ primaryMgmtAccountPlaceholder }}</code
                >); the Recovery StackSet from your <strong>Recovery management account</strong> (<code>{{
                  recoveryMgmtAccountPlaceholder
                }}</code
                >).
              </span>
            </div>
          }
          <p>Create two StackSets to manage {{ uiService.productName }} access.</p>
          <div class="command-group">
            <h4>
              <i class="fa-solid fa-terminal"></i>
              Primary Access StackSet
            </h4>
            @if (isTwoMgmtAccounts) {
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Primary management account</strong> (<code>{{
                    primaryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
            }
            <div class="command-block">
              <div class="command-actions">
                <button
                  class="copy-btn"
                  [copy-clipboard]="createPrimaryStackSetCommand"
                  (copied)="onCopied('create-primary')"
                >
                  <i
                    class="fa-utility fa-semibold"
                    [class.fa-copy]="!isCopied('create-primary')"
                    [class.fa-check]="isCopied('create-primary')"
                  ></i>
                  {{ isCopied('create-primary') ? 'Copied!' : 'Copy' }}
                </button>
                <button
                  class="download-btn"
                  (click)="downloadCommand('create-primary-stackset', createPrimaryStackSetCommand)"
                  title="Download as shell script"
                >
                  <i class="fa-solid fa-download"></i>
                  Download
                </button>
              </div>
              <pre><code>{{ createPrimaryStackSetCommand }}</code></pre>
            </div>
          </div>
          <div class="command-group">
            <h4>
              <i class="fa-solid fa-terminal"></i>
              Recovery Access StackSet
            </h4>
            @if (isTwoMgmtAccounts) {
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Recovery management account</strong> (<code>{{
                    recoveryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
            }
            <div class="command-block">
              <div class="command-actions">
                <button
                  class="copy-btn"
                  [copy-clipboard]="createRecoveryStackSetCommand"
                  (copied)="onCopied('create-recovery')"
                >
                  <i
                    class="fa-utility fa-semibold"
                    [class.fa-copy]="!isCopied('create-recovery')"
                    [class.fa-check]="isCopied('create-recovery')"
                  ></i>
                  {{ isCopied('create-recovery') ? 'Copied!' : 'Copy' }}
                </button>
                <button
                  class="download-btn"
                  (click)="downloadCommand('create-recovery-stackset', createRecoveryStackSetCommand)"
                  title="Download as shell script"
                >
                  <i class="fa-solid fa-download"></i>
                  Download
                </button>
              </div>
              <pre><code>{{ createRecoveryStackSetCommand }}</code></pre>
            </div>
          </div>
          <!-- Troubleshooting Section -->
          <details class="troubleshooting-section" #troubleshoot2>
            <summary>
              <i class="fa-solid fa-wrench"></i>
              <span>Troubleshooting Step 3</span>
              <i
                class="fa-solid chevron-icon"
                [class.fa-chevron-down]="troubleshoot2.open"
                [class.fa-chevron-right]="!troubleshoot2.open"
              ></i>
            </summary>
            <div class="troubleshooting-content">
              <p class="last-updated"><em>Last updated: February 2, 2026</em></p>
              <div class="ts-list">
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Confirm StackSets were created successfully</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>Run this from your <strong>management account</strong> to list your StackSets:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="listStackSetsCommand"
                          (copied)="onCopied('list-stacksets')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('list-stacksets')"
                            [class.fa-check]="isCopied('list-stacksets')"
                          ></i>
                          {{ isCopied('list-stacksets') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ listStackSetsCommand }}</code></pre>
                    </div>
                    <p class="expected-output">Expected: Shows your StackSets with <code>Status: ACTIVE</code></p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">View StackSet configuration and template details</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>Check the configuration of a StackSet including its template URL and execution role:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="describeStackSetCommand"
                          (copied)="onCopied('describe-stackset')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('describe-stackset')"
                            [class.fa-check]="isCopied('describe-stackset')"
                          ></i>
                          {{ isCopied('describe-stackset') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ describeStackSetCommand }}</code></pre>
                    </div>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Fix "StackSet already exists" error</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>If you see "StackSet already exists", the StackSet was previously created. You can either:</p>
                    <ul>
                      <li>Use the existing StackSet by entering its name in the input fields above, or</li>
                      <li>Delete the existing StackSet (only if it has no stack instances) and create a new one</li>
                    </ul>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="deleteStackSetCommand"
                          (copied)="onCopied('delete-stackset')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('delete-stackset')"
                            [class.fa-check]="isCopied('delete-stackset')"
                          ></i>
                          {{ isCopied('delete-stackset') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ deleteStackSetCommand }}</code></pre>
                    </div>
                    <div class="troubleshooting-warning">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      <span
                        >You can only delete a StackSet after removing all its stack instances. See
                        <a class="step-link" (click)="scrollToStep('deploy', true)">Step 4 troubleshooting</a> for how
                        to delete stack instances.</span
                      >
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </details>
          <div class="step-continue-row">
            <button type="button" class="arpio-secondary-button" (click)="continueToStep('create', 'deploy')">
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Step 4: Deploy to Accounts -->
    <div class="step-section" [class.expanded]="isExpanded('deploy')" [class.regen-pending]="regenerationInProgress">
      <div class="step-header" [style.top.px]="stickyOffset || null" (click)="toggleSection('deploy')">
        <div class="step-number">4</div>
        <div class="step-title">
          <h3>Deploy Stack Instances</h3>
          <p class="step-subtitle">Run from your management account to deploy to member accounts</p>
        </div>
        <i
          class="fa-solid"
          [class.fa-chevron-down]="isExpanded('deploy')"
          [class.fa-chevron-right]="!isExpanded('deploy')"
        ></i>
      </div>
      <div class="step-content-wrapper" [@slideDown]="isExpanded('deploy') ? 'expanded' : 'collapsed'">
        <div class="step-content">
          @if (!isTwoMgmtAccounts) {
            <div class="important-note">
              <i class="fa-solid fa-circle-info"></i>
              <span>
                Run these commands from your <strong>management account</strong>. StackSets will automatically create
                the access stacks in the specified member
                @if (uniqueAccountIds.length === 1) {
                  account ({{ uniqueAccountIds[0] }})
                }
                @if (uniqueAccountIds.length > 1) {
                  accounts ({{ uniqueAccountIds.join(' and ') }})
                }
                .
              </span>
            </div>
          }
          @if (isTwoMgmtAccounts) {
            <div class="important-note">
              <i class="fa-solid fa-circle-info"></i>
              <span>
                Run each command from its matching management account. StackSets will create the access stacks in the
                specified member accounts (<code>{{ sourceAccountId }}</code>
                @if (targetAccountId !== sourceAccountId) {
                  and <code>{{ targetAccountId }}</code>
                }
                ).
              </span>
            </div>
          }
          <!-- Primary -->
          <div class="deploy-section">
            <div class="deploy-header">
              <h4>
                <i class="fa-solid fa-location-dot"></i>
                Primary Access
              </h4>
              <div class="deploy-target">
                <code>{{ sourceAccountId }}</code> / <code>{{ sourceRegion }}</code>
              </div>
            </div>
            @if (isTwoMgmtAccounts) {
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Primary management account</strong> (<code>{{
                    primaryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
            }
            @if (needsSourceOnboarding) {
              <!-- Info/Warning if existing CloudFormation stack exists but not StackSet-managed -->
              @if (hasSourceCfnButNotStackSet) {
                <div class="warning-notice">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  <span>
                    An Arpio CloudFormation Stack (<code>{{ sourceStackName }}</code
                    >) exists in this account. Deploying via StackSets may fail if there are conflicts. Consider using
                    standalone Stacks for this app, or remove the existing Stack first.
                    <a
                      class="help-link"
                      (click)="scrollToTroubleshooting('stack-conflicts')"
                      title="How to resolve stack conflicts"
                    >
                      <i class="fa-solid fa-circle-question"></i>
                    </a>
                  </span>
                </div>
              }
              <div class="notice-block">
                <div class="notice-text">
                  @if (sourceRetryText) {
                    <strong class="retry-text">{{ sourceRetryText }}</strong>
                  }
                  <span
                    >After running this command, allow 1–3 minutes for changes to propagate, then verify access is
                    configured.</span
                  >
                </div>
                <button
                  class="arpio-button test-access-btn"
                  [disabled]="testingSource"
                  (click)="onTestAccess('source')"
                >
                  @if (testingSource) {
                    <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
                  } @else {
                    TEST ACCESS
                  }
                </button>
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button
                    class="copy-btn"
                    [copy-clipboard]="deployPrimaryCommand"
                    (copied)="onCopied('deploy-primary')"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('deploy-primary')"
                      [class.fa-check]="isCopied('deploy-primary')"
                    ></i>
                    {{ isCopied('deploy-primary') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('deploy-primary', deployPrimaryCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ deployPrimaryCommand }}</code></pre>
              </div>
            } @else {
              <div class="configured-notice">
                <i class="fa-solid fa-circle-check"></i>
                <span>Access configured via StackSets</span>
              </div>
            }
          </div>
          <!-- Recovery -->
          <div class="deploy-section">
            <div class="deploy-header">
              <h4>
                <i class="fa-solid fa-location-crosshairs"></i>
                Recovery Access
              </h4>
              <div class="deploy-target">
                <code>{{ targetAccountId }}</code> / <code>{{ targetRegion }}</code>
              </div>
            </div>
            @if (isTwoMgmtAccounts) {
              <div class="location-notice">
                <i class="fa-solid fa-circle-info"></i>
                <span
                  >Run this from your <strong>Recovery management account</strong> (<code>{{
                    recoveryMgmtAccountPlaceholder
                  }}</code
                  >).</span
                >
              </div>
            }
            @if (needsTargetOnboarding) {
              <!-- Info/Warning if existing CloudFormation stack exists but not StackSet-managed -->
              @if (hasTargetCfnButNotStackSet) {
                <div class="warning-notice">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  <span>
                    An Arpio CloudFormation Stack (<code>{{ targetStackName }}</code
                    >) exists in this account. Deploying via StackSets may fail if there are conflicts. Consider using
                    standalone Stacks for this app, or remove the existing Stack first.
                    <a
                      class="help-link"
                      (click)="scrollToTroubleshooting('stack-conflicts')"
                      title="How to resolve Stack conflicts"
                    >
                      <i class="fa-solid fa-circle-question"></i>
                    </a>
                  </span>
                </div>
              }
              <div class="notice-block">
                <div class="notice-text">
                  @if (targetRetryText) {
                    <strong class="retry-text">{{ targetRetryText }}</strong>
                  }
                  <span
                    >After running this command, allow 1–3 minutes for changes to propagate, then verify access is
                    configured.</span
                  >
                </div>
                <button
                  class="arpio-button test-access-btn"
                  [disabled]="testingTarget"
                  (click)="onTestAccess('target')"
                >
                  @if (testingTarget) {
                    <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
                  } @else {
                    TEST ACCESS
                  }
                </button>
              </div>
              <div class="command-block">
                <div class="command-actions">
                  <button
                    class="copy-btn"
                    [copy-clipboard]="deployRecoveryCommand"
                    (copied)="onCopied('deploy-recovery')"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [class.fa-copy]="!isCopied('deploy-recovery')"
                      [class.fa-check]="isCopied('deploy-recovery')"
                    ></i>
                    {{ isCopied('deploy-recovery') ? 'Copied!' : 'Copy' }}
                  </button>
                  <button
                    class="download-btn"
                    (click)="downloadCommand('deploy-recovery', deployRecoveryCommand)"
                    title="Download as shell script"
                  >
                    <i class="fa-solid fa-download"></i>
                    Download
                  </button>
                </div>
                <pre><code>{{ deployRecoveryCommand }}</code></pre>
              </div>
            } @else {
              <div class="configured-notice">
                <i class="fa-solid fa-circle-check"></i>
                <span>Access configured via StackSets</span>
              </div>
            }
          </div>
          <!-- Troubleshooting Section -->
          <details class="troubleshooting-section" #troubleshoot3>
            <summary>
              <i class="fa-solid fa-wrench"></i>
              <span>Troubleshooting Step 4</span>
              <i
                class="fa-solid chevron-icon"
                [class.fa-chevron-down]="troubleshoot3.open"
                [class.fa-chevron-right]="!troubleshoot3.open"
              ></i>
            </summary>
            <div class="troubleshooting-content">
              <p class="last-updated"><em>Last updated: February 2, 2026</em></p>
              <!-- Important Note about async operations -->
              <div class="async-note">
                <i class="fa-solid fa-clock"></i>
                <div>
                  <strong>StackSet operations are asynchronous</strong>
                  <p>
                    After running a deploy command, AWS processes the request in the background. It typically takes 1-3
                    minutes for Stack instances to be created. Use the verification commands below to check progress.
                  </p>
                </div>
              </div>
              <div class="ts-list margin-top">
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Check if Stack instances deployed successfully</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    @if (!isTwoMgmtAccounts) {
                      <p>
                        Run this from your <strong>management account</strong> to see the status of all Stack instances
                        in a StackSet:
                      </p>
                    }
                    @if (isTwoMgmtAccounts) {
                      <p>
                        Run each command from its matching management account to see the status of all Stack instances
                        in each StackSet:
                      </p>
                    }
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="listStackInstancesCommand"
                          (copied)="onCopied('list-instances')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('list-instances')"
                            [class.fa-check]="isCopied('list-instances')"
                          ></i>
                          {{ isCopied('list-instances') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ listStackInstancesCommand }}</code></pre>
                    </div>
                    <p class="expected-output">
                      Expected: <code>Status: CURRENT</code> means success. <code>Status: OUTDATED</code> or
                      <code>INOPERABLE</code> indicates a problem.
                    </p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Get error details for OUTDATED or failed instances</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>If a Stack instance shows <code>OUTDATED</code> or failed, get the detailed error message:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="describeStackInstanceCommand"
                          (copied)="onCopied('describe-instance')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('describe-instance')"
                            [class.fa-check]="isCopied('describe-instance')"
                          ></i>
                          {{ isCopied('describe-instance') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ describeStackInstanceCommand }}</code></pre>
                    </div>
                    <p class="expected-output">Look at <code>StatusReason</code> for the specific error message.</p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">View recent StackSet operations and results</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>View recent operations to understand what happened:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="listStackSetOperationsCommand"
                          (copied)="onCopied('list-operations')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('list-operations')"
                            [class.fa-check]="isCopied('list-operations')"
                          ></i>
                          {{ isCopied('list-operations') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ listStackSetOperationsCommand }}</code></pre>
                    </div>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Retry deployment after fixing IAM roles or transient errors</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>
                      If deployment failed due to a transient error or after fixing IAM roles, retry the deployment:
                    </p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="retryPrimaryInstanceCommand"
                          (copied)="onCopied('retry-primary')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('retry-primary')"
                            [class.fa-check]="isCopied('retry-primary')"
                          ></i>
                          {{ isCopied('retry-primary') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ retryPrimaryInstanceCommand }}</code></pre>
                    </div>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="retryRecoveryInstanceCommand"
                          (copied)="onCopied('retry-recovery')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('retry-recovery')"
                            [class.fa-check]="isCopied('retry-recovery')"
                          ></i>
                          {{ isCopied('retry-recovery') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ retryRecoveryInstanceCommand }}</code></pre>
                    </div>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Remove Stack instances and start fresh</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>If you need to completely remove a Stack instance and start fresh:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="deleteStackInstanceCommand"
                          (copied)="onCopied('delete-instance')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('delete-instance')"
                            [class.fa-check]="isCopied('delete-instance')"
                          ></i>
                          {{ isCopied('delete-instance') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ deleteStackInstanceCommand }}</code></pre>
                    </div>
                    <div class="troubleshooting-warning">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      <span
                        >Deleting Stack instances will remove the access Stacks from the member accounts. Wait for the
                        delete operation to complete before re-running the deploy command.</span
                      >
                    </div>
                  </div>
                </details>
                <details class="ts-item" id="stack-conflicts">
                  <summary>
                    <span class="ts-title">Fix conflicts with existing Arpio CloudFormation Stacks</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <p>
                      If you have existing Arpio CloudFormation Stacks in a member account, the StackSets deployment may
                      fail due to conflicts. To resolve this, delete these Stack instances first, then redeploy via
                      StackSets.
                    </p>
                    <div class="troubleshooting-warning">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      <span
                        ><strong>Important:</strong> Deleting the existing CloudFormation Stack will temporarily remove
                        Arpio access from that account until the StackSets deployment completes.</span
                      >
                    </div>
                    <p class="step-label">
                      <strong>Step 1:</strong> Delete the existing CloudFormation Stack (run from the affected member
                      account):
                    </p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="deleteSourceConflictStackCommand"
                          (copied)="onCopied('delete-source-conflict')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('delete-source-conflict')"
                            [class.fa-check]="isCopied('delete-source-conflict')"
                          ></i>
                          {{ isCopied('delete-source-conflict') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ deleteSourceConflictStackCommand }}</code></pre>
                    </div>
                    <p class="step-label"><strong>Step 2:</strong> Wait for the Stack deletion to complete:</p>
                    <div class="command-block">
                      <div class="command-actions">
                        <button
                          class="copy-btn"
                          [copy-clipboard]="waitForStackDeleteCommand"
                          (copied)="onCopied('wait-stack-delete')"
                        >
                          <i
                            class="fa-utility fa-semibold"
                            [class.fa-copy]="!isCopied('wait-stack-delete')"
                            [class.fa-check]="isCopied('wait-stack-delete')"
                          ></i>
                          {{ isCopied('wait-stack-delete') ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                      <pre><code>{{ waitForStackDeleteCommand }}</code></pre>
                    </div>
                    <p class="step-label">
                      <strong>Step 3:</strong> Re-run the StackSets deployment command (from your management account):
                    </p>
                    <p class="expected-output">
                      Use the deploy commands from
                      <a class="step-link" (click)="scrollToStep('deploy')">Step 4 above</a> to create new Stack
                      instances via StackSets.
                    </p>
                  </div>
                </details>
                <details class="ts-item">
                  <summary>
                    <span class="ts-title">Common error messages and how to fix them</span>
                    <i class="fa-solid fa-chevron-right ts-chevron"></i>
                  </summary>
                  <div class="ts-content">
                    <div class="error-solutions">
                      <div class="error-solution">
                        <div class="error-header">
                          <i class="fa-solid fa-circle-exclamation"></i>
                          <code>Account {{ sourceAccountId }} should have 'ArpioStackSetExecutionRole' role</code>
                        </div>
                        <div class="solution">
                          <i class="fa-solid fa-arrow-right"></i>
                          <span
                            >The Execution Role doesn't exist in the member account.
                            <a class="step-link" (click)="scrollToStep('prerequisites')">Go back to Step 2</a> and run
                            the Execution Role command from account <code>{{ sourceAccountId }}</code
                            >.</span
                          >
                        </div>
                      </div>
                      <div class="error-solution">
                        <div class="error-header">
                          <i class="fa-solid fa-circle-exclamation"></i>
                          <code
                            >User: arn:aws:iam::...:role/ArpioStackSetAdministrationRole is not authorized to perform:
                            sts:AssumeRole</code
                          >
                        </div>
                        @if (!isTwoMgmtAccounts) {
                          <div class="solution">
                            <i class="fa-solid fa-arrow-right"></i>
                            <span
                              >The Execution Role's trust policy doesn't allow your management account. Verify the trust
                              policy points to <code>{{ primaryMgmtAccountPlaceholder }}</code> using the
                              <a class="step-link" (click)="scrollToStep('prerequisites', true)"
                                >Step 2 troubleshooting commands</a
                              >.</span
                            >
                          </div>
                        }
                        @if (isTwoMgmtAccounts) {
                          <div class="solution">
                            <i class="fa-solid fa-arrow-right"></i>
                            <span
                              >The Execution Role's trust policy doesn't allow the matching management account. In the
                              Primary member account (<code>{{ sourceAccountId }}</code
                              >) verify the trust points to <code>{{ primaryMgmtAccountPlaceholder }}</code
                              >; in the Recovery member account (<code>{{ targetAccountId }}</code
                              >) verify it points to <code>{{ recoveryMgmtAccountPlaceholder }}</code
                              >. Use the
                              <a class="step-link" (click)="scrollToStep('prerequisites', true)"
                                >Step 2 troubleshooting commands</a
                              >.</span
                            >
                          </div>
                        }
                      </div>
                      <div class="error-solution">
                        <div class="error-header">
                          <i class="fa-solid fa-circle-exclamation"></i>
                          <code>StackSet not found</code>
                        </div>
                        <div class="solution">
                          <i class="fa-solid fa-arrow-right"></i>
                          <span
                            >The StackSet hasn't been created yet.
                            <a class="step-link" (click)="scrollToStep('create')">Complete Step 3</a> first, then
                            <a class="step-link" (click)="scrollToStep('deploy')">return to Step 4</a>.</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </details>
        </div>
      </div>
      <!-- Spinner overlay — sits over the entire Step 4 accordion (header + content) while regen runs. -->
      @if (regenerationInProgress) {
        <div class="regen-spinner-overlay">
          <div class="regen-spinner-pill">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <p>Preparing templates&hellip;</p>
          </div>
        </div>
      }
    </div>
  }
  <!-- End Steps 1-3 -->

  <!-- Migration Cleanup Section - only shown after migration completes -->
  @if (migrationComplete) {
    <div class="migration-cleanup-section">
      <div class="setup-notice success">
        <div class="notice-icon">
          <i class="fa-solid fa-circle-check"></i>
        </div>
        <div class="notice-content">
          <strong>Migration Complete</strong>
          <p>Your access is now managed by StackSets. Both accounts are configured and working.</p>
        </div>
        <button type="button" class="arpio-button" (click)="finishSetup.emit()">FINISH SET UP</button>
      </div>
      <div class="migration-details">
        <!-- Configuration Summary -->
        <div class="config-summary">
          <div class="summary-cards">
            <!-- Management Account(s) -->
            @if (!isTwoMgmtAccounts && (primaryManagementAccountId || managementAccountId)) {
              <div class="summary-item full-width">
                <div class="summary-label">
                  <i class="fa-solid fa-shield-halved"></i>
                  Management Account
                </div>
                <code class="summary-value">{{ primaryManagementAccountId || managementAccountId }}</code>
              </div>
            }
            @if (isTwoMgmtAccounts) {
              @if (primaryManagementAccountId || managementAccountId) {
                <div class="summary-item">
                  <div class="summary-label">
                    <i class="fa-solid fa-shield-halved"></i>
                    Primary Management Account
                  </div>
                  <code class="summary-value">{{ primaryManagementAccountId || managementAccountId }}</code>
                </div>
              }
              @if (recoveryManagementAccountId) {
                <div class="summary-item">
                  <div class="summary-label">
                    <i class="fa-solid fa-shield-halved"></i>
                    Recovery Management Account
                  </div>
                  <code class="summary-value">{{ recoveryManagementAccountId }}</code>
                </div>
              }
            }
            <!-- Primary StackSet -->
            @if (primaryStackSetName) {
              <div class="summary-item">
                <div class="summary-label">
                  <i class="fa-solid fa-location-dot"></i>
                  Primary StackSet
                </div>
                <code class="summary-value">{{ primaryStackSetName }}</code>
              </div>
            }
            <!-- Recovery StackSet -->
            @if (recoveryStackSetName) {
              <div class="summary-item">
                <div class="summary-label">
                  <i class="fa-solid fa-location-crosshairs"></i>
                  Recovery StackSet
                </div>
                <code class="summary-value">{{ recoveryStackSetName }}</code>
              </div>
            }
            <!-- Primary Access Account -->
            <div class="summary-item">
              <div class="summary-label">
                <i class="fa-solid fa-location-dot"></i>
                Primary Access
              </div>
              <div class="summary-value">
                <div class="summary-status success">
                  <i class="fa-solid fa-circle-check"></i>
                  <code>{{ sourceAccountId }}</code> / <code>{{ sourceRegion }}</code>
                </div>
              </div>
            </div>
            <!-- Recovery Access Account -->
            <div class="summary-item">
              <div class="summary-label">
                <i class="fa-solid fa-location-crosshairs"></i>
                Recovery Access
              </div>
              <div class="summary-value">
                <div class="summary-status success">
                  <i class="fa-solid fa-circle-check"></i>
                  <code>{{ targetAccountId }}</code> / <code>{{ targetRegion }}</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!--
                                                      TODO: Add future updates note once StackSets-specific documentation is available.
                                                      The existing docs (https://docs.arpio.io/update-cf) are for CloudFormation template updates,
                                                      not StackSets. We need StackSets-specific guidance for updating templates.

                                                      Suggested note to add:
                                                      <p class="future-updates-note">
                                                        <i class="fa-solid fa-circle-info"></i>
                                                        <span>
                                                          When access templates are updated in the future, you'll need to update your StackSets.
                                                          <a href="[STACKSETS_DOCS_URL]" target="_blank">Learn more about updating StackSets</a>
                                                        </span>
                                                      </p>
                                                      -->
</div>
